import { api } from '@goaltree/backend/convex/_generated/api'
import { CHEER_KINDS, type CheerKind, dayShareUrl } from '@goaltree/backend/convex/lib/dayShare'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Drawer, DrawerContent, DrawerTitle } from '@goaltree/ui/components/drawer'
import { cn } from '@goaltree/ui/lib/utils'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import {
  ArrowLeftIcon,
  CalendarCheckIcon,
  Camera,
  CopyIcon,
  ExternalLinkIcon,
  Grid3x3Icon,
  HashIcon,
  ImageIcon,
  LinkIcon,
  ListChecksIcon,
  Loader2Icon,
  QrCodeIcon,
  Share2Icon,
  TargetIcon,
  XIcon,
} from 'lucide-react'
import qrcode from 'qrcode-generator'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { IconTip } from '@/components/icon-tip'
import { SettingsGroup, SettingsRow, Toggle, rowId } from '@/components/settings/primitives'
import { useShareStreakStory } from '@/components/streak-story-prompt'
import { ACCENT_OPTIONS, useAccentTheme } from '@/hooks/use-accent-theme'
import { useIsMobile } from '@/hooks/use-mobile'
import { dayShareCards, openShareCard } from '@/lib/share-card'

type Section = 'habits' | 'grid' | 'goals' | 'tasks' | 'numbers'

const SECTIONS: { key: Section; icon: React.ReactNode }[] = [
  { key: 'habits', icon: <CalendarCheckIcon className="h-4 w-4" /> },
  { key: 'grid', icon: <Grid3x3Icon className="h-4 w-4" /> },
  { key: 'goals', icon: <TargetIcon className="h-4 w-4" /> },
  { key: 'tasks', icon: <ListChecksIcon className="h-4 w-4" /> },
  { key: 'numbers', icon: <HashIcon className="h-4 w-4" /> },
]

const CHEER_EMOJI: Record<CheerKind, string> = { water: '💧', sun: '☀️', fire: '🔥', summit: '🏔️' }

// Today's gt.today cheers, e.g. "💧 2  🔥 1"; null when there are none.
const cheerLine = (counts: Record<CheerKind, number>) =>
  CHEER_KINDS.filter((k) => counts[k] > 0)
    .map((k) => `${CHEER_EMOJI[k]} ${counts[k]}`)
    .join('  ') || null

async function shareOrCopy(url: string, title: string, copied: string) {
  if (navigator.share) {
    try {
      await navigator.share({ title, url: withShareUtm(url, 'day_link') })
      return
    } catch (e) {
      if ((e as Error).name === 'AbortError') return
    }
  }
  await navigator.clipboard.writeText(withShareUtm(url, 'day_link'))
  toast.success(copied)
}

function useSetDayShare() {
  return useMutation(api.daySharing.setDayShare).withOptimisticUpdate((store, patch) => {
    const cur = store.getQuery(api.daySharing.getMyDayShare, {})
    if (cur)
      store.setQuery(
        api.daySharing.getMyDayShare,
        {},
        {
          ...cur,
          settings: { ...cur.settings, ...patch },
        },
      )
  })
}

export function DayShareSettings() {
  const { t } = useTranslation()
  const [sheet, setSheet] = useState(false)
  const data = useQuery(api.daySharing.getMyDayShare)
  const setDayShare = useSetDayShare()
  const s = data?.settings
  const url = data?.handle ? dayShareUrl(data.handle) : null
  const save = (patch: Parameters<typeof setDayShare>[0]) =>
    void setDayShare(patch).catch(() => toast.error(t('settings.prefError')))

  return (
    <SettingsGroup label={t('dayShare.title')}>
      <SettingsRow
        id={rowId('dayShare.toggle')}
        icon={<Share2Icon className="h-4 w-4" />}
        label={t('dayShare.toggle')}
        description={t('dayShare.desc')}
      >
        <Toggle
          label={t('dayShare.toggle')}
          checked={!!s?.on}
          onChange={(on) => save({ on })}
          disabled={!data || !url}
        />
      </SettingsRow>
      {data && !url && (
        <SettingsRow
          label={<span className="text-xs text-muted-foreground">{t('dayShare.needHandle')}</span>}
        />
      )}
      {s?.on && url && (
        <>
          <SettingsRow
            icon={<LinkIcon className="h-4 w-4" />}
            label={
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:underline"
              >
                {url.replace('https://', '')}
                <ExternalLinkIcon className="h-3 w-3 text-muted-foreground" />
              </a>
            }
          >
            <button
              type="button"
              onClick={() => setSheet(true)}
              className="flex h-7 cursor-pointer items-center gap-1 rounded-md px-2 text-xs font-semibold text-primary-text hover:bg-muted"
            >
              <CopyIcon className="h-3.5 w-3.5" />
              {t('common.share')}
            </button>
          </SettingsRow>
          {SECTIONS.map(({ key, icon }) => (
            <SettingsRow
              key={key}
              id={rowId(`dayShare.${key}`)}
              icon={icon}
              label={t(`dayShare.${key}`)}
              description={t(`dayShare.${key}Desc`)}
            >
              <Toggle
                label={t(`dayShare.${key}`)}
                checked={!!s[key]}
                onChange={(v) => save({ [key]: v })}
              />
            </SettingsRow>
          ))}
        </>
      )}
      <DayShareSheet open={sheet} onOpenChange={setSheet} />
    </SettingsGroup>
  )
}

export function QuickLinksSettings() {
  const { t, i18n } = useTranslation()
  const links = useQuery(api.quickLinks.listMine)
  const revoke = useMutation(api.quickLinks.revoke).withOptimisticUpdate((store, { habitId }) => {
    const cur = store.getQuery(api.quickLinks.listMine, {})
    if (cur)
      store.setQuery(
        api.quickLinks.listMine,
        {},
        cur.filter((l) => l.habitId !== habitId),
      )
    if (store.getQuery(api.quickLinks.getForHabit, { habitId }) !== undefined)
      store.setQuery(api.quickLinks.getForHabit, { habitId }, null)
  })
  if (!links?.length) return null
  return (
    <SettingsGroup label={t('quickLog.listTitle')}>
      {links.map((l) => (
        <SettingsRow
          key={l.habitId}
          icon={<LinkIcon className="h-4 w-4" />}
          label={l.title}
          description={
            l.lastUsedAt
              ? t('quickLog.lastUsed', {
                  date: new Date(l.lastUsedAt).toLocaleDateString(i18n.language),
                })
              : t('quickLog.neverUsed')
          }
        >
          <button
            type="button"
            aria-label={t('quickLog.revoke')}
            title={t('quickLog.revoke')}
            onClick={() => {
              if (window.confirm(t('quickLog.revokeConfirm')))
                void revoke({ habitId: l.habitId }).catch(() => toast.error(t('common.error')))
            }}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-destructive"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </SettingsRow>
      ))}
    </SettingsGroup>
  )
}

const OPTIONS: Section[] = ['habits', 'grid', 'goals', 'tasks', 'numbers']

// The story image takes the app accent by its category colour; mono and the
// daily rotation fall back to the card's pink.
function useAccentRef() {
  const { accent, rotate } = useAccentTheme()
  return (!rotate && ACCENT_OPTIONS.find((a) => a.id === accent)?.category) || 'pink'
}

function QrCode({ text, className }: { text: string; className?: string }) {
  const { n, d } = useMemo(() => {
    const qr = qrcode(0, 'M')
    qr.addData(text)
    qr.make()
    const n = qr.getModuleCount()
    let d = ''
    for (let r = 0; r < n; r++)
      for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`
    return { n, d }
  }, [text])
  return (
    <svg viewBox={`-3 -3 ${n + 6} ${n + 6}`} shapeRendering="crispEdges" className={className}>
      <rect x={-3} y={-3} width={n + 6} height={n + 6} fill="#fff" />
      <path d={d} fill="#000" />
    </svg>
  )
}

function MiniRing({ done, due }: { done: number | null; due: number | null }) {
  const c = 2 * Math.PI * 44
  const frac = due ? (done ?? 0) / due : 0
  return (
    <div className="flex h-[84px] w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-border/60 bg-background">
      <svg width="34" height="34" viewBox="0 0 112 112" aria-hidden="true">
        <circle cx="56" cy="56" r="44" fill="none" strokeWidth="14" className="stroke-muted" />
        {frac > 0 && (
          <circle
            cx="56"
            cy="56"
            r="44"
            fill="none"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - frac)}
            transform="rotate(-90 56 56)"
            className="stroke-primary"
          />
        )}
      </svg>
      <span className="text-[10px] font-bold tabular-nums">
        {due ? `${done ?? 0}/${due}` : '–'}
      </span>
    </div>
  )
}

export function DayShareSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const data = useQuery(api.daySharing.getMyDayShare, open ? {} : 'skip')
  const setDayShare = useSetDayShare()
  const accentRef = useAccentRef()
  const [view, setView] = useState<'main' | 'qr'>('main')
  const s = data?.settings
  const on = !!s?.on
  const handle = data?.handle ?? null
  const url = handle ? dayShareUrl(handle) : null
  const day = useQuery(api.daySharing.getPublicDay, open && on && handle ? { handle } : 'skip')
  const cheers = day ? cheerLine(day.cheers) : null
  const unsharedMilestones = useQuery(
    api.instagramStories.getUnsharedStreakMilestones,
    open ? {} : 'skip',
  )
  const shareStreakStory = useShareStreakStory()
  const [sharingId, setSharingId] = useState<string | null>(null)

  const shareStory = async (m: NonNullable<typeof unsharedMilestones>[number]) => {
    setSharingId(m._id)
    await shareStreakStory(m, () => onOpenChange(false))
    setSharingId(null)
  }

  useEffect(() => {
    if (open) setView('main')
  }, [open])

  const save = (patch: Parameters<typeof setDayShare>[0]) =>
    setDayShare(patch).catch(() => {
      toast.error(t('settings.prefError'))
    })

  const copy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(withShareUtm(url, 'day_link'))
      toast.success(t('common.copied'))
    } catch {
      toast.error(t('share.copyFailed'))
    }
  }

  // Same story-card picker as habits: closes this sheet, then opens on the day card.
  const story = () => {
    onOpenChange(false)
    setTimeout(() => openShareCard(dayShareCards(accentRef)), 200)
  }

  const stop = () => {
    if (!window.confirm(t('dayShare.stopConfirm'))) return
    void save({ on: false })
  }

  const Title = isMobile ? DrawerTitle : DialogTitle
  const action =
    'flex h-[76px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50'

  const toggles = s && (
    <div className="flex flex-col rounded-2xl border border-border/60 bg-card">
      <span className="px-3.5 pt-3 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {t('dayShare.whatPeopleSee')}
      </span>
      {OPTIONS.map((key) => (
        <div
          key={key}
          className="flex min-h-12 items-center gap-3 border-t border-border/60 px-3.5"
        >
          <span className="flex-1 text-sm font-medium">{t(`dayShare.opt.${key}`)}</span>
          <span className="text-xs text-muted-foreground">
            {key === 'habits'
              ? (day?.due ?? '')
              : t(`dayShare.optHint.${key as Exclude<Section, 'habits'>}`)}
          </span>
          <Toggle
            label={t(`dayShare.opt.${key}`)}
            checked={!!s[key]}
            onChange={(v) => void save({ [key]: v })}
          />
        </div>
      ))}
    </div>
  )

  const main = !data ? (
    <div className="flex h-40 items-center justify-center">
      <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
    </div>
  ) : !url ? (
    <div className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-card p-4">
      <p className="text-sm text-muted-foreground">{t('dayShare.needHandle')}</p>
      <Link
        to="/settings"
        hash="profile"
        onClick={() => onOpenChange(false)}
        className="flex h-11 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground"
      >
        {t('dayShare.setHandle')}
      </Link>
    </div>
  ) : (
    <>
      <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card p-3">
        <MiniRing done={day?.done ?? null} due={day?.due ?? null} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="truncate text-[15px] font-semibold hover:underline"
          >
            {url.replace('https://', '')}
          </a>
          <span className="text-xs leading-snug text-muted-foreground">
            {on ? t('dayShare.linkHint') : t('dayShare.offHint')}
          </span>
          {cheers && (
            <span className="text-xs font-semibold">
              {t('dayShare.cheersToday')} {cheers}
            </span>
          )}
        </div>
        {on && (
          <button
            type="button"
            onClick={() => void copy()}
            aria-label={t('dayShare.copy')}
            title={t('dayShare.copy')}
            className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-border/60 bg-muted/50 transition-colors hover:bg-muted"
          >
            <CopyIcon className="size-[18px]" />
          </button>
        )}
      </div>
      {on ? (
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => void shareOrCopy(url, t('dayShare.title'), t('common.copied'))}
            className={cn(action, 'bg-primary font-bold text-primary-foreground hover:opacity-90')}
          >
            <Share2Icon className="size-5" />
            {t('dayShare.shareLink')}
          </button>
          <button
            type="button"
            onClick={story}
            className={cn(action, 'border border-border/60 bg-card hover:bg-muted')}
          >
            <ImageIcon className="size-5" />
            {t('dayShare.story')}
          </button>
          <button
            type="button"
            onClick={() => setView('qr')}
            className={cn(action, 'border border-border/60 bg-card hover:bg-muted')}
          >
            <QrCodeIcon className="size-5" />
            {t('dayShare.qr')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => void save({ on: true })}
          className="flex h-12 cursor-pointer items-center justify-center rounded-2xl bg-primary text-sm font-bold text-primary-foreground transition-opacity hover:opacity-90"
        >
          {t('dayShare.turnOn')}
        </button>
      )}
      {toggles}
      {on && unsharedMilestones?.length && (
        <div className="flex flex-col gap-2 rounded-2xl border border-border/60 bg-card p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.1em]">
            {t('streakStory.title')}
          </p>
          {unsharedMilestones.map((m) => (
            <button
              key={m._id}
              type="button"
              onClick={() => void shareStory(m)}
              disabled={sharingId === m._id}
              className="flex w-full items-center gap-3 rounded-xl border border-border/60 bg-muted/40 px-3 py-2.5 text-sm text-left transition-colors hover:bg-muted/60 disabled:opacity-50"
            >
              <Camera className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="flex-1 text-sm font-medium">
                {t('streakStory.milestoneReached', { days: m.streakDays })}
              </span>
              {sharingId === m._id ? (
                <Loader2Icon className="h-4 w-4 animate-spin" />
              ) : (
                <span className="text-xs text-muted-foreground">{t('streakStory.button')}</span>
              )}
            </button>
          ))}
        </div>
      )}
      {on && (
        <button
          type="button"
          onClick={stop}
          className="h-11 cursor-pointer text-[13px] font-medium text-muted-foreground transition-colors hover:text-destructive"
        >
          {t('dayShare.stop')}
        </button>
      )}
    </>
  )

  const qr = url && (
    <div className="flex flex-col items-center gap-4 pb-2">
      <QrCode text={url} className="size-56 rounded-2xl" />
      <span className="text-[15px] font-semibold">{url.replace('https://', '')}</span>
      <span className="text-center text-xs text-muted-foreground">{t('dayShare.qrHint')}</span>
    </div>
  )

  const body = (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 pr-8">
        <div className="flex items-center gap-2">
          {view === 'qr' && (
            <button
              type="button"
              onClick={() => setView('main')}
              aria-label={t('common.back')}
              className="-ml-1 flex size-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ArrowLeftIcon className="size-4" />
            </button>
          )}
          <Title className="text-xl font-black tracking-tight">
            {view === 'qr' ? t('dayShare.qr') : t('common.share')}
          </Title>
        </div>
        {on && view === 'main' && (
          <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
            <span className="size-[7px] rounded-full bg-emerald-500" />
            {t('dayShare.live')}
          </span>
        )}
      </div>
      {view === 'qr' && qr ? qr : main}
    </div>
  )

  return isMobile ? (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto w-full max-w-lg overflow-hidden data-[vaul-drawer-direction=bottom]:max-h-[94dvh] data-[vaul-drawer-direction=bottom]:rounded-t-3xl">
        <div className="overflow-y-auto px-4 pt-3 pb-[max(env(safe-area-inset-bottom),20px)] text-sm text-foreground">
          {body}
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-md overflow-y-auto">{body}</DialogContent>
    </Dialog>
  )
}

// Today header: opens the "Share my day" sheet.
export function ShareDayButton() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <>
      <IconTip label={t('common.share')}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('common.share')}
        >
          <Share2Icon className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </IconTip>
      <DayShareSheet open={open} onOpenChange={setOpen} />
    </>
  )
}

// Today: /today?share=day (the day-cheers notification) opens the sheet.
export function DayShareDeepLink() {
  const navigate = useNavigate()
  const open = useRouterState({
    select: (r) => (r.location.search as { share?: string }).share === 'day',
  })
  return (
    <DayShareSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) void navigate({ to: '/today', replace: true })
      }}
    />
  )
}
